import { useEffect, useState } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { ArrowRight, MapPin, Sparkles, Tag, Clock, Flower2, Cigarette, Cookie, Droplet, FlaskConical, Wind } from "lucide-react";
import SEOHead from "../components/SEOHead";
import StoreSwitcher from "../components/StoreSwitcher";
import ProductCard from "../components/ProductCard";
import { getLocation, getMenu, getSpecials, getCategories } from "../lib/api";
import { classifySpecial } from "../lib/promos";

// Maps the ZenZest brand URL prefix to the legacy directory store slug.
const STORE_ALIAS = {
    queens: "queens-long-island",
    "staten-island": "staten-island",
};

export default function StoreHome() {
    const { pathname } = useLocation();
    const storePrefix = pathname.split("/").filter(Boolean)[0];
    const storeSlug = STORE_ALIAS[storePrefix];
    const [store, setStore] = useState(null);
    const [menu, setMenu] = useState(null);
    const [specials, setSpecials] = useState([]);
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        if (!storeSlug) return;
        let cancelled = false;
        getLocation(storeSlug).then((d) => !cancelled && setStore(d)).catch(() => {});
        getMenu(storeSlug, { limit: 8, sort_key: "POPULAR" })
            .then((d) => !cancelled && setMenu(d))
            .catch(() => {});
        getSpecials(storeSlug)
            .then((d) => !cancelled && setSpecials(d?.specials || []))
            .catch(() => {});
        getCategories()
            .then((d) => !cancelled && setCategories(d || []))
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [storeSlug]);

    if (!storeSlug) return <Navigate to="/" replace />;

    const products = menu?.products || [];
    const specialsById = Object.fromEntries(
        (specials || []).map((s) => {
            const c = classifySpecial(s);
            return c ? [c.id, c] : [s.id, s];
        })
    );
    const canonical = `https://weeddispensarynearme.com/${storePrefix}`;
    const heroTagline =
        storePrefix === "queens"
            ? "Cannabis, considered. In Queens & on the Island."
            : "Cannabis, considered. On Staten Island.";

    return (
        <>
            <SEOHead
                title={`ZenZest ${store?.short_name || "NY"} — NY OCM-licensed cannabis dispensary`}
                description={`Live menu, deals, and ZenCircle rewards at ZenZest ${store?.short_name || ""}. Pickup or delivery. NY-licensed.`}
                canonical={canonical}
            />

            {/* Hero */}
            <section className="bg-zen-bg relative">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 pt-10 sm:pt-12 pb-12 sm:pb-16">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
                        <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive inline-flex items-center gap-2">
                            <MapPin size={11} /> {store?.short_name || "ZenZest"} · NY
                        </p>
                        <StoreSwitcher currentSlug={storeSlug} tone="dark" />
                    </div>
                    <h1
                        data-testid="store-home-h1"
                        className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-zen-ink leading-[1.05] tracking-tight max-w-3xl mb-7"
                    >
                        {heroTagline}
                    </h1>
                    <p className="text-base sm:text-lg text-zen-ink-soft max-w-2xl mb-9 leading-relaxed">
                        Live menu, members-only deals, and ZenCircle rewards. NY OCM-licensed.
                        Free pickup in 20 minutes — delivery same day across {store?.short_name?.split(" & ")[0] || "the borough"}.
                    </p>
                    <div className="flex flex-wrap gap-3">
                        <Link
                            to={`/menu/${storeSlug}`}
                            data-testid="cta-shop-menu"
                            className="bg-zen-ink text-zen-cream px-7 py-3.5 text-[11px] uppercase tracking-[0.22em] font-medium hover:bg-zen-olive-dark transition-colors inline-flex items-center gap-2"
                        >
                            Shop the menu <ArrowRight size={14} />
                        </Link>
                        <Link
                            to="/loyalty"
                            className="border border-zen-ink text-zen-ink px-7 py-3.5 text-[11px] uppercase tracking-[0.22em] font-medium hover:bg-zen-ink hover:text-zen-cream transition-colors inline-flex items-center gap-2"
                        >
                            <Sparkles size={12} /> Earn points
                        </Link>
                    </div>
                </div>
            </section>

            {/* Shop by category */}
            {categories.length > 0 && (
                <section
                    data-testid="category-grid-section"
                    className="bg-zen-bg border-t border-zen-line"
                >
                    <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-10 sm:py-14">
                        <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
                            <div>
                                <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-3">
                                    Shop by category
                                </p>
                                <h2 className="font-serif text-3xl sm:text-4xl font-light text-zen-ink leading-tight tracking-tight">
                                    Find your thing.
                                </h2>
                            </div>
                            <Link
                                to={`/menu/${storeSlug}`}
                                className="text-[11px] uppercase tracking-[0.22em] text-zen-olive hover:text-zen-olive-dark border-b border-zen-olive/40 pb-1 inline-flex items-center gap-2"
                            >
                                Browse everything <ArrowRight size={12} />
                            </Link>
                        </div>
                        <div
                            data-testid="category-grid"
                            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3"
                        >
                            {categories.map((cat) => {
                                const Icon =
                                    {
                                        FLOWER: Flower2,
                                        PRE_ROLLS: Cigarette,
                                        VAPORIZERS: Wind,
                                        EDIBLES: Cookie,
                                        CONCENTRATES: FlaskConical,
                                        TINCTURES: Droplet,
                                    }[cat.slug] || Tag;
                                return (
                                    <Link
                                        key={cat.slug}
                                        to={`/menu/${storeSlug}?category=${cat.slug}`}
                                        data-testid={`category-${cat.slug.toLowerCase()}`}
                                        className="group bg-zen-surface border border-zen-line p-6 hover:border-zen-olive hover:bg-zen-bg transition-colors flex flex-col items-start"
                                    >
                                        <Icon
                                            size={22}
                                            className="text-zen-olive mb-4 group-hover:scale-110 transition-transform"
                                            strokeWidth={1.4}
                                        />
                                        <h3 className="font-serif text-xl text-zen-ink font-light leading-tight mb-1 tracking-tight">
                                            {cat.name}
                                        </h3>
                                        <p className="text-[11px] text-zen-ink-soft leading-snug">
                                            {cat.tagline}
                                        </p>
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {/* Featured products */}
            {products.length > 0 && (
                <section className="bg-zen-surface border-y border-zen-line">
                    <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-10 sm:py-14">
                        <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
                            <div>
                                <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-3">
                                    Popular this week
                                </p>
                                <h2 className="font-serif text-3xl sm:text-4xl font-light text-zen-ink leading-tight">
                                    Featured cannabis.
                                </h2>
                            </div>
                            <Link
                                to={`/menu/${storeSlug}`}
                                className="text-[11px] uppercase tracking-[0.22em] text-zen-olive hover:text-zen-olive-dark border-b border-zen-olive/40 hover:border-zen-olive-dark pb-1 inline-flex items-center gap-2"
                            >
                                Browse the full menu <ArrowRight size={12} />
                            </Link>
                        </div>
                        <div
                            data-testid="store-home-products"
                            className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3"
                        >
                            {products.slice(0, 8).map((p, i) => (
                                <ProductCard
                                    key={`${p.id}-${i}`}
                                    product={p}
                                    storeSlug={storeSlug}
                                    specialsById={specialsById}
                                />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Deals teaser */}
            {specials.length > 0 && (
                <section className="bg-zen-bg">
                    <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-16">
                        <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-3 inline-flex items-center gap-2">
                            <Tag size={12} /> Active deals · {specials.length}
                        </p>
                        <h2 className="font-serif text-3xl sm:text-4xl font-light text-zen-ink leading-tight mb-8">
                            Tonight&rsquo;s deals.
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {specials.slice(0, 6).map((s) => (
                                <Link
                                    key={s.id}
                                    to={`/menu/${storeSlug}/special/${s.id}`}
                                    className="bg-zen-surface border border-zen-line p-6 hover:border-zen-olive transition-colors"
                                >
                                    <p className="text-[10px] uppercase tracking-[0.22em] text-zen-olive mb-3">
                                        {s.type || "Deal"}
                                    </p>
                                    <p className="font-serif text-xl text-zen-ink font-light leading-snug line-clamp-2">
                                        {s.name}
                                    </p>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Loyalty teaser */}
            <section className="bg-zen-elevated text-zen-cream">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <p className="text-[11px] uppercase tracking-[0.3em] opacity-75 mb-3 inline-flex items-center gap-2">
                            <Sparkles size={11} /> ZenCircle
                        </p>
                        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light leading-tight mb-5 tracking-tight">
                            1 point per dollar. Friday drops. Early access to limited flower.
                        </h2>
                        <p className="text-sm sm:text-base opacity-80 mb-7 max-w-2xl leading-relaxed">
                            Free to join. Check your balance any time via text — no app, no
                            password, no email spam.
                        </p>
                        <Link
                            to="/loyalty"
                            className="bg-zen-cream text-zen-ink px-7 py-3.5 text-[11px] uppercase tracking-[0.22em] font-medium hover:bg-zen-bg transition-colors inline-flex items-center gap-2"
                        >
                            <Sparkles size={12} /> Check my points
                        </Link>
                    </div>
                    <div className="lg:col-span-5 grid grid-cols-3 gap-3 text-center">
                        {[
                            { n: "1×", l: "Bud" },
                            { n: "1.25×", l: "Bloom" },
                            { n: "1.5×", l: "Harvest" },
                        ].map((t) => (
                            <div key={t.l} className="border border-zen-cream/20 p-5">
                                <p className="font-serif text-3xl font-light leading-none mb-2">
                                    {t.n}
                                </p>
                                <p className="text-[10px] uppercase tracking-[0.22em] opacity-75">
                                    {t.l}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Store info teaser */}
            {store && (
                <section className="bg-zen-bg">
                    <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-16 grid grid-cols-1 md:grid-cols-2 gap-10">
                        <div>
                            <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-3 inline-flex items-center gap-2">
                                <MapPin size={11} /> Visit us
                            </p>
                            <h3 className="font-serif text-2xl sm:text-3xl font-light text-zen-ink leading-tight mb-4">
                                {store.name}
                            </h3>
                            <p className="text-sm text-zen-ink-soft leading-relaxed">
                                {[store.street, store.city, store.state, store.zip]
                                    .filter(Boolean)
                                    .join(", ")}
                            </p>
                            <Link
                                to={`/locations/${storeSlug}`}
                                className="mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-zen-olive hover:text-zen-olive-dark border-b border-zen-olive/40 pb-1"
                            >
                                Hours, parking & directions <ArrowRight size={12} />
                            </Link>
                        </div>
                        <div>
                            <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-3 inline-flex items-center gap-2">
                                <Clock size={11} /> Today
                            </p>
                            <p className="font-serif text-2xl sm:text-3xl font-light text-zen-ink leading-tight mb-4">
                                Open until{" "}
                                {(() => {
                                    const h = store?.hours;
                                    if (!h) return "10pm";
                                    if (typeof h === "string") return h.split(" ")[1] || h;
                                    // Array of {day, open, close} from getLocation()
                                    if (Array.isArray(h)) {
                                        const today = new Date().toLocaleDateString(
                                            "en-US",
                                            { weekday: "short" }
                                        );
                                        const slot =
                                            h.find((d) => d.day === today) || h[0];
                                        return slot?.close || "10pm";
                                    }
                                    return "10pm";
                                })()}
                            </p>
                            <Link
                                to={`/${storePrefix}/contact`}
                                data-testid="store-home-contact-link"
                                className="text-[11px] uppercase tracking-[0.22em] text-zen-olive hover:text-zen-olive-dark border-b border-zen-olive/40 pb-1 inline-flex items-center gap-2"
                            >
                                Get in touch <ArrowRight size={12} />
                            </Link>
                        </div>
                    </div>
                </section>
            )}
        </>
    );
}
